/**
 * Browser printing for the on-screen invoice/chalan/quotation previews.
 *
 * The thermal `print` util in ./print.js posts to the printer API and is meant for
 * receipt rolls. This one renders the already-styled preview markup on A4 through
 * the browser print dialog instead.
 */

const READY_POLL_MS = 50;
const READY_TIMEOUT_MS = 3000;
const DIALOG_CLEANUP_MS = 1000;

/** App styles live in <style> tags in dev and <link> tags in a production build. */
function collectDocumentStyles() {
  return Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style')
  )
    .map(node => node.outerHTML)
    .join("\n");
}

function buildPrintDocument({ title, bodyHtml, marginMm }) {
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    ${collectDocumentStyles()}
    <style>
      @page {
        size: A4;
        margin: ${marginMm}mm;
      }
      html,
      body {
        margin: 0;
        padding: 0;
        background: #fff;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      /* Reflow the preview card, which is sized for the screen, into the page box. */
      .a4-print-root {
        width: 100%;
      }
      /* The previews cap their width for the screen (max-w-4xl, 760px); without
         this they print at that size and leave the rest of the sheet empty. */
      .a4-print-root > * {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }
      .a4-print-root table {
        page-break-inside: auto;
      }
      .a4-print-root tr {
        page-break-inside: avoid;
      }
    </style>
  </head>
  <body>
    <div class="a4-print-root">${bodyHtml}</div>
  </body>
</html>`;
}

/**
 * Polls instead of listening for load events: the iframe document is written
 * synchronously, so its load event may already have fired by the time we look.
 */
function waitUntilReady(doc) {
  const deadline = Date.now() + READY_TIMEOUT_MS;

  const isReady = () => {
    const stylesReady = Array.from(
      doc.querySelectorAll('link[rel="stylesheet"]')
    ).every(link => link.sheet);
    const imagesReady = Array.from(doc.images || []).every(img => img.complete);
    return stylesReady && imagesReady;
  };

  return new Promise(resolve => {
    const check = () => {
      if (isReady() || Date.now() > deadline) {
        resolve();
        return;
      }
      setTimeout(check, READY_POLL_MS);
    };
    check();
  });
}

/**
 * Prints `element` on A4 via a hidden iframe, so the print dialog never has to
 * fight a popup blocker and the live Vue tree is left alone.
 *
 * @param {HTMLElement} element Preview node to print.
 * @param {{ title?: string, marginMm?: number }} [options]
 * @returns {Promise<boolean>} false when there was nothing to print.
 */
export function printElementAsA4(element, options = {}) {
  if (!element) return Promise.resolve(false);

  const { title = document.title, marginMm = 10 } = options;

  // Sized to the sheet rather than 0x0: a zero-width frame gives the document no
  // layout viewport, so the content lays out narrow and prints in a corner.
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(
    buildPrintDocument({ title, bodyHtml: element.outerHTML, marginMm })
  );
  doc.close();

  return waitUntilReady(doc).then(() => {
    const win = iframe.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
    // Removing the frame while the dialog is still open cancels the job.
    setTimeout(() => iframe.remove(), DIALOG_CLEANUP_MS);
    return true;
  });
}

export default printElementAsA4;
